import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowLeft, Clock, ShieldCheck, PencilSimple, Play, ArrowsClockwise,
    ToggleLeft, Check, WarningCircle, PauseCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Superadmin → Schedulers.
 *
 * Every cron / interval job the backend registers, with:
 *   • current trigger (editable inline)
 *   • next run time
 *   • last run summary + error
 *   • Run-now, Reset-to-default, Pause/Resume
 *
 * Fires against services/superadmin_schedulers.py — persists overrides to
 * the `scheduler_configs` collection so edits survive a redeploy.
 */

const CATEGORY_META = {
    interview: { label: "Interview", color: "bg-cobalt/10 text-cobalt border-cobalt/30" },
    candidate: { label: "Candidate", color: "bg-splash/10 text-splash border-splash/30" },
    recruiter: { label: "Recruiter", color: "bg-orange-100 text-orange-700 border-orange-300" },
    email: { label: "Email", color: "bg-emerald-100 text-emerald-700 border-emerald-300" },
    resume_studio: { label: "Resume Studio", color: "bg-purple-100 text-purple-700 border-purple-300" },
    analytics: { label: "Analytics", color: "bg-blue-100 text-blue-700 border-blue-300" },
    healer: { label: "Healer", color: "bg-gray-100 text-gray-700 border-gray-300" },
    other: { label: "Other", color: "bg-gray-100 text-gray-700 border-gray-300" },
};

function fmtIST(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-IN", {
            timeZone: "Asia/Kolkata",
            day: "2-digit", month: "short",
            hour: "2-digit", minute: "2-digit", hour12: true,
        });
    } catch { return iso; }
}

function triggerLabel(t) {
    if (!t) return "—";
    if (t.type === "interval") {
        if (t.hours) return `every ${t.hours}h`;
        if (t.minutes) return `every ${t.minutes} min`;
        if (t.seconds) return `every ${t.seconds}s`;
        return "interval";
    }
    if (t.type === "cron") {
        // NOTE: raw hour/minute is stored in UTC by apscheduler (default tz).
        // We display it as-is with a "UTC" suffix so admins editing know the
        // frame of reference — the human-friendly IST fire time is shown in
        // the adjacent "Next run" column via fmtIST().
        const dow = t.day_of_week ? `${t.day_of_week} · ` : "";
        const h = String(t.hour ?? "*").padStart(2, "0");
        const m = String(t.minute ?? "*").padStart(2, "0");
        return `${dow}${h}:${m} UTC`;
    }
    return t.type;
}

export default function SuperAdminSchedulers() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tz, setTz] = useState("Asia/Kolkata");
    const [editing, setEditing] = useState(null);   // { job, draft }
    const [runningId, setRunningId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/superadmin/schedulers");
            setItems(r.data?.items || []);
            setTz(r.data?.scheduler_timezone || "Asia/Kolkata");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load schedulers");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const byCategory = useMemo(() => {
        const g = {};
        for (const it of items) (g[it.category] ||= []).push(it);
        return g;
    }, [items]);

    const patch = async (id, body) => {
        try {
            await api.patch(`/superadmin/schedulers/${id}`, body);
            toast.success("Saved");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        }
    };

    const runNow = async (id) => {
        setRunningId(id);
        try {
            const r = await api.post(`/superadmin/schedulers/${id}/run-now`);
            const s = r.data?.summary || {};
            toast.success(`Ran ${id} — ${JSON.stringify(s)}`, { duration: 6000 });
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Run failed");
        } finally { setRunningId(null); }
    };

    const reset = async (id) => {
        if (!window.confirm(`Reset "${id}" to the code-shipped default trigger?`)) return;
        try {
            await api.post(`/superadmin/schedulers/${id}/reset`);
            toast.success("Reset to default");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reset failed");
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-5 sm:py-8 overflow-x-hidden" data-testid="superadmin-schedulers-page">
            <div className="mb-5 sm:mb-6">
                <Link
                    to="/superadmin"
                    data-testid="schedulers-back"
                    className="text-[11px] font-black uppercase tracking-wider text-[#525252] hover:text-ink inline-flex items-center gap-1.5 mb-3"
                >
                    <ArrowLeft size={12} weight="bold" /> Superadmin
                </Link>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-splash mb-1 inline-flex items-center gap-1.5">
                    <ShieldCheck size={12} weight="fill" /> Platform-level access
                </p>
                <h1 className="font-heading text-2xl sm:text-4xl font-black tracking-tight flex items-center gap-2 flex-wrap">
                    <Clock size={24} weight="bold" className="sm:hidden" />
                    <Clock size={28} weight="bold" className="hidden sm:inline" />
                    <span>Schedulers</span>
                </h1>
                <p className="text-xs sm:text-sm text-[#525252] mt-2 max-w-2xl">
                    Every APScheduler job registered on this backend.
                    Timezone: <span className="font-black text-ink">{tz}</span>.
                    Edits are persisted to <code className="text-[11px] bg-[#F5F5F5] px-1">scheduler_configs</code> and
                    survive a restart.
                </p>
            </div>

            {loading && (
                <p className="text-sm text-[#A3A3A3]" data-testid="schedulers-loading">Loading…</p>
            )}

            {!loading && Object.keys(byCategory).map((cat) => (
                <section key={cat} className="mb-8" data-testid={`schedulers-cat-${cat}`}>
                    <div className="flex items-baseline gap-3 mb-3">
                        <h2 className="font-heading text-lg font-black tracking-tight uppercase">
                            {CATEGORY_META[cat]?.label || cat}
                        </h2>
                        <span className="text-[11px] font-black uppercase text-[#A3A3A3]">
                            {byCategory[cat].length} job{byCategory[cat].length > 1 ? "s" : ""}
                        </span>
                    </div>
                    <div className="bg-white border border-[#E5E5E5] overflow-x-auto">
                        <table className="w-full text-sm min-w-[860px]">
                            <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                                <tr className="text-left">
                                    {["Job", "Trigger", "Next run", "Last run", "Actions"].map((h) => (
                                        <th key={h} className="px-4 py-3 text-[10px] font-black uppercase tracking-wider text-[#525252]">
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {byCategory[cat].map((it) => (
                                    <tr key={it.id}
                                        data-testid={`scheduler-row-${it.id}`}
                                        className="border-b border-[#F5F5F5] hover:bg-[#FAFAFA]">
                                        <td className="px-4 py-3">
                                            <div className="font-black text-ink text-sm">{it.description}</div>
                                            <code className="text-[10px] text-[#A3A3A3]">{it.id}</code>
                                            {!it.enabled && (
                                                <span className="ml-2 text-[10px] font-black uppercase tracking-wider text-orange-600 inline-flex items-center gap-1">
                                                    <PauseCircle size={11} weight="fill" /> Paused
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="font-black tabular-nums text-ink">{triggerLabel(it.current_trigger)}</div>
                                            {it.override_trigger && (
                                                <div className="text-[10px] text-cobalt font-black uppercase tracking-wider mt-0.5">
                                                    Overridden · default {triggerLabel(it.default_trigger)}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-[#525252] tabular-nums text-xs">
                                            {fmtIST(it.next_run_at)}
                                        </td>
                                        <td className="px-4 py-3 text-xs">
                                            {it.last_run_at ? (
                                                <div>
                                                    <div className="text-[#525252]">{fmtIST(it.last_run_at)}</div>
                                                    <div className="flex items-center gap-1 mt-0.5">
                                                        {it.last_run_ok ? (
                                                            <Check size={11} weight="bold" className="text-emerald-600" />
                                                        ) : (
                                                            <WarningCircle size={11} weight="fill" className="text-red-600" />
                                                        )}
                                                        <span className="text-[10px] text-[#A3A3A3] truncate max-w-[180px]"
                                                              title={JSON.stringify(it.last_run_summary || it.last_run_error)}>
                                                            {it.last_run_ok
                                                                ? Object.entries(it.last_run_summary || {}).map(([k,v]) => `${k}=${v}`).join(" · ")
                                                                : (it.last_run_error || "failed")}
                                                        </span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <span className="text-[#A3A3A3]">— never</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <button
                                                    onClick={() => setEditing({ job: it, draft: { ...(it.override_trigger || it.default_trigger || {type:"interval",minutes:15}) } })}
                                                    data-testid={`scheduler-edit-${it.id}`}
                                                    className="inline-flex items-center gap-1 px-2 py-1 border border-[#E5E5E5] hover:bg-[#F5F5F5] text-[10px] font-black uppercase tracking-wider"
                                                >
                                                    <PencilSimple size={11} weight="bold" /> Edit
                                                </button>
                                                <button
                                                    onClick={() => runNow(it.id)}
                                                    disabled={runningId === it.id}
                                                    data-testid={`scheduler-run-${it.id}`}
                                                    className="inline-flex items-center gap-1 px-2 py-1 border border-cobalt/30 bg-cobalt/5 hover:bg-cobalt/10 text-cobalt text-[10px] font-black uppercase tracking-wider disabled:opacity-50"
                                                >
                                                    <Play size={11} weight="fill" /> {runningId === it.id ? "Running…" : "Run now"}
                                                </button>
                                                <button
                                                    onClick={() => patch(it.id, { enabled: !it.enabled })}
                                                    data-testid={`scheduler-toggle-${it.id}`}
                                                    className="inline-flex items-center gap-1 px-2 py-1 border border-[#E5E5E5] hover:bg-[#F5F5F5] text-[10px] font-black uppercase tracking-wider"
                                                >
                                                    <ToggleLeft size={11} weight="bold" /> {it.enabled ? "Pause" : "Resume"}
                                                </button>
                                                {it.override_trigger && (
                                                    <button
                                                        onClick={() => reset(it.id)}
                                                        data-testid={`scheduler-reset-${it.id}`}
                                                        className="inline-flex items-center gap-1 px-2 py-1 border border-[#E5E5E5] hover:bg-[#F5F5F5] text-[10px] font-black uppercase tracking-wider"
                                                    >
                                                        <ArrowsClockwise size={11} weight="bold" /> Reset
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            ))}

            {editing && (
                <EditModal
                    job={editing.job}
                    onClose={() => setEditing(null)}
                    onSave={async (override) => {
                        await patch(editing.job.id, { override_trigger: override });
                        setEditing(null);
                    }}
                />
            )}
        </div>
    );
}

function EditModal({ job, onClose, onSave }) {
    const [t, setT] = useState(job.override_trigger || job.default_trigger || { type: "interval", minutes: 15 });
    const isInterval = t.type === "interval";

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4"
             data-testid="scheduler-edit-modal">
            <div className="bg-white border-2 border-ink w-full max-w-lg p-6">
                <div className="mb-4">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cobalt mb-1">Edit trigger</p>
                    <h2 className="font-heading text-xl font-black tracking-tight">{job.description}</h2>
                    <code className="text-[10px] text-[#A3A3A3]">{job.id}</code>
                </div>

                <div className="flex gap-2 mb-4">
                    {["interval", "cron"].map((tt) => (
                        <button
                            key={tt}
                            onClick={() => setT({ type: tt })}
                            data-testid={`edit-tab-${tt}`}
                            className={`px-3 py-1.5 text-[11px] font-black uppercase tracking-wider border ${
                                t.type === tt ? "bg-ink text-white border-ink" : "border-[#E5E5E5] hover:bg-[#F5F5F5]"
                            }`}
                        >{tt}</button>
                    ))}
                </div>

                {isInterval ? (
                    <div className="grid grid-cols-3 gap-2 mb-4">
                        {["minutes", "hours", "seconds"].map((f) => (
                            <label key={f} className="text-[10px] font-black uppercase tracking-wider text-[#525252]">
                                {f}
                                <input
                                    type="number"
                                    min="0"
                                    value={t[f] || ""}
                                    onChange={(e) => setT({ ...t, [f]: e.target.value ? parseInt(e.target.value) : undefined })}
                                    data-testid={`edit-interval-${f}`}
                                    className="mt-1 w-full border-2 border-ink px-2 py-1.5 text-sm font-mono"
                                />
                            </label>
                        ))}
                    </div>
                ) : (
                    <div className="space-y-3 mb-4">
                        <label className="block text-[10px] font-black uppercase tracking-wider text-[#525252]">
                            Day of week (e.g. <code>mon</code>, <code>mon,wed,fri</code>, blank = daily)
                            <input
                                type="text"
                                value={t.day_of_week || ""}
                                onChange={(e) => setT({ ...t, day_of_week: e.target.value || undefined })}
                                data-testid="edit-cron-dow"
                                className="mt-1 w-full border-2 border-ink px-2 py-1.5 text-sm font-mono"
                            />
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                            <label className="text-[10px] font-black uppercase tracking-wider text-[#525252]">
                                Hour (UTC · 0-23)
                                <input
                                    type="number" min="0" max="23"
                                    value={t.hour ?? ""}
                                    onChange={(e) => setT({ ...t, hour: e.target.value === "" ? undefined : parseInt(e.target.value) })}
                                    data-testid="edit-cron-hour"
                                    className="mt-1 w-full border-2 border-ink px-2 py-1.5 text-sm font-mono"
                                />
                            </label>
                            <label className="text-[10px] font-black uppercase tracking-wider text-[#525252]">
                                Minute (0-59)
                                <input
                                    type="number" min="0" max="59"
                                    value={t.minute ?? ""}
                                    onChange={(e) => setT({ ...t, minute: e.target.value === "" ? undefined : parseInt(e.target.value) })}
                                    data-testid="edit-cron-minute"
                                    className="mt-1 w-full border-2 border-ink px-2 py-1.5 text-sm font-mono"
                                />
                            </label>
                        </div>
                    </div>
                )}

                <div className="text-[11px] text-[#525252] mb-4">
                    Preview: <span className="font-black text-ink">{triggerLabel(t)}</span>
                    <span className="text-[#A3A3A3] ml-2">· default {triggerLabel(job.default_trigger)}</span>
                </div>

                <div className="flex justify-end gap-2">
                    <button
                        onClick={onClose}
                        data-testid="edit-cancel"
                        className="px-4 py-2 border border-[#E5E5E5] hover:bg-[#F5F5F5] text-[11px] font-black uppercase tracking-wider"
                    >Cancel</button>
                    <button
                        onClick={() => onSave(t)}
                        data-testid="edit-save"
                        className="px-4 py-2 bg-cobalt text-white text-[11px] font-black uppercase tracking-wider hover:opacity-90"
                    >Save override</button>
                </div>
            </div>
        </div>
    );
}
